import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { BehaviorSubject, exhaustMap, forkJoin, merge, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { WorkWeekdayModel } from '../../models/complex/work-weekday.model';
import { WorkdayModel } from '../../models/low-level/workday.model';
import { ConfigService } from '../../services/config/config.service';
import { WeeklyWorkTimeConcurrentStorageService } from './weekly-work-time-concurrent-storage.service';
import { isDefined } from '../../utilities/is-defined';
import { WeeklyWorkTimeFileStorageService } from './weekly-work-time-file-storage.service';
import { TimesheetAndExpensesService } from '../timesheet/timesheet-and-expenses.service';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { EventService } from '../event/event.service';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from '../../models/complex/time-recording-entry.model';

export class StatusBar {
    public constructor(
        public type?: string,
        public text?: string,
    ) {}
}

@Injectable({
    providedIn: 'root',
})
export class WeeklyWorkTimeService {
    private workTimeModel: WorkWeekdayModel;
    private updateWeeklyWorkTimeValueSubject: BehaviorSubject<WorkdayModel[]>;
    public updateWeeklyWorkTimeValue$: Observable<WorkdayModel[]>;
    public displayWeeklyWorkTime: boolean;
    public weeklyWorkTimeValue$: Observable<WorkdayModel[]>;

    public constructor(
        private readonly weeklyWorkTimeConcurrencyStorageService: WeeklyWorkTimeConcurrentStorageService,
        private readonly weeklyWorkTimeFileStorageService: WeeklyWorkTimeFileStorageService,
        private readonly timesheetAndExpensesService: TimesheetAndExpensesService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly configService: ConfigService,
        private readonly eventService: EventService,
    ) {
        this.updateWeeklyWorkTimeValueSubject = new BehaviorSubject<WorkdayModel[]>([]);
        this.updateWeeklyWorkTimeValue$ = this.updateWeeklyWorkTimeValueSubject.asObservable();

        this.eventService.updateOnSubmittingTimesheet$
            .pipe(mergeMap((timesheet) => this.updateWorkDayDurationOnTimesheetSubmission(timesheet)))
            .subscribe();
    }

    /**
     * This function is to get weekly work time list from file storage
     */
    public init(): Observable<void> {
        this.displayWeeklyWorkTime = this.configService.getDisplayWeeklyWorkTime();

        if (!this.displayWeeklyWorkTime) {
            return of(undefined);
        }
        // Each time the user syncs the application,
        // It should also sync the weekly work time to ensure the weekly work hours are synced to the current date.
        this.eventService.synchronizationRequested$
            .pipe(
                exhaustMap(() => this.syncWeeklyWorkTime()),
                catchError((error: Error) => {
                    this.log.error(`WeeklyWorkTimeService: synchronizationRequested$: Error: ${error?.message}`);
                    return of(undefined);
                }),
            )
            .subscribe();

        this.weeklyWorkTimeValue$ = merge(
            this.updateWeeklyWorkTimeValue$,
            this.timesheetAndExpensesService
                .selectCurrentTimesheet$()
                .pipe(mergeMap((timesheet) => this.setWorkTimeDependency(timesheet))),
        );

        return this.getWeeklyWorkModelFromStore().pipe(
            mergeMap((workTimeModel) => {
                if (!isDefined(this.workTimeModel) || this.workTimeModel.workdayModelList.length === 0) {
                    this.workTimeModel = workTimeModel;
                    return this.createWeeklyWorkTimeList();
                }
                return of(undefined);
            }),
            tap(() => this.updateWeeklyWorkTimeValueSubject.next(this.workTimeModel.workdayModelList)),
        );
    }

    private setWorkTimeDependency(timesheet: Readonly<TimesheetModel>): Observable<WorkdayModel[]> {
        if (!isDefined(this.workTimeModel)) {
            return of([]);
        }

        this.updateTotalDurationForSpecificDate(
            timesheet.startOfWork,
            this.getTotalDurationOfTimesheetEntries(timesheet),
        );

        return this.saveWeeklyWorkTimeList().pipe(map(() => this.workTimeModel.workdayModelList));
    }

    private getWeeklyWorkModelFromStore(): Observable<WorkWeekdayModel> {
        return forkJoin([
            this.weeklyWorkTimeFileStorageService.getWeeklyWorkModel(),
            this.weeklyWorkTimeConcurrencyStorageService.getWeeklyWorkModel(),
        ]).pipe(
            map(([localWeeklyWorkHours, remoteWeeklyWorkHours]) => {
                if (localWeeklyWorkHours.timeStamp > remoteWeeklyWorkHours.timeStamp) {
                    return localWeeklyWorkHours;
                }
                if (remoteWeeklyWorkHours.timeStamp > localWeeklyWorkHours.timeStamp) {
                    return remoteWeeklyWorkHours;
                }
                return localWeeklyWorkHours;
            }),
        );
    }

    /**
     * When the user clicks "Start Work", check if weekly work time exists for the selected start date.
     * if it does not exist, create a new work time entry.
     */
    public syncWeeklyWorkTime(): Observable<void> {
        if (!this.displayWeeklyWorkTime) {
            return of(undefined);
        }

        // Check if weekly work is already available for that day
        const isTodayWorkTimeAvailable = this.workTimeModel.workdayModelList.some((workTime) =>
            workTime.workDate.isSame(moment(), 'days'),
        );

        if (!isTodayWorkTimeAvailable) {
            this.log.debug(
                `WeeklyWorkTimeService: syncWeeklyWorkTime: work time for date ${moment().toString()} is not available, re-creating work time data`,
            );
            return this.createWeeklyWorkTimeList().pipe(
                tap(() => this.updateWeeklyWorkTimeValueSubject.next(this.workTimeModel.workdayModelList)),
            );
        }
        return of(undefined);
    }

    /**
     * The weekly work time data has to be cleared after sunday 23:59,
     * So the new weekly work list will be created on every monday or every new week and save it to local storage
     *
     * The For loop is to add all the missing dates
     * Example:  If user re-installed pace app on Friday, then the data from Monday to Thursday will be missing in the list,
     * So, using for loop we are creating all the previous missing weeklyWorkTime with the particular date and with 0 hours duration
     */
    private createWeeklyWorkTimeList(): Observable<void> {
        // If it is new week, clear the old week's work time list data, if any
        if (this.workTimeModel.workdayModelList.length > 0 && this.isNewWeek()) {
            this.log.debug(`WeeklyWorkTimeService: Creating weekly work time for new week ${moment().week()}`);
            this.workTimeModel.workdayModelList = [];
        }

        // As per the ISO_8601: https://en.wikipedia.org/wiki/ISO_8601#Week_dates
        // Monday is considered as Beginning/Start of the week i.e  Monday as day 1
        // Sunday is considered as End of the week i.e Sunday as Day 7

        const currentWeekDay = moment().isoWeekday();

        for (let i = currentWeekDay - 1; i >= 0; i--) {
            const today = moment();
            today.subtract(i, 'day');

            const currentDateWorkModel = this.getWorkDayObjectByDate(today);

            if (!isDefined(currentDateWorkModel)) {
                this.log.debug(`WeeklyWorkTimeService: Creating new work day for ${today}`);
                const workTimeDetails = new WorkdayModel(today);
                this.workTimeModel.workdayModelList.push(workTimeDetails);
            }
        }
        return this.saveWeeklyWorkTimeList();
    }

    /**
     * This function gets called when timesheet gets submits
     * Possibilities , user can submit timesheet multiple times, so we need to check, if particular date has any previously saved timesheet duration
     * If yes, then we need to add the old timesheet duration to new duration
     *
     * @param timesheet
     */
    private updateWorkDayDurationOnTimesheetSubmission(timesheet: TimesheetModel): Observable<void> {
        if (!timesheet.startOfWork.isValid()) {
            this.log.warn(`WeeklyWorkTimeService: updateWorkDayDurationOnTimesheetSubmission : date is invalid.`);
            return of(undefined);
        }
        const workDay = this.getWorkDayObjectByDate(timesheet.startOfWork);

        if (!isDefined(workDay)) {
            this.log.warn(
                `WeeklyWorkTimeService: updateWorkDayDurationOnTimesheetSubmission : weekly work time for date ${timesheet.startOfWork.toString()} is not found`,
            );
            return of(undefined);
        }

        const duration = this.getTotalDurationOfTimesheetEntries(timesheet);

        this.log.debug(
            `Adding currentTimesheetDuration: ${duration} to
            submittedTimesheetDuration: ${workDay.submittedTimesheetDuration} for date: ${timesheet.startOfWork.toString()} `,
        );
        // update submitted duration
        workDay.submittedTimesheetDuration = moment.duration(workDay.submittedTimesheetDuration).add(duration);
        workDay.currentTimesheetDuration = moment.duration(0);

        return this.saveWeeklyWorkTimeList();
    }

    /**
     * This function is to save the weekly work time to Local storage as well as to memory variable
     */
    private saveWeeklyWorkTimeList(): Observable<void> {
        const weeklyWorkModel = new WorkWeekdayModel(this.workTimeModel.workdayModelList, Date.now());
        return this.weeklyWorkTimeFileStorageService.saveWeeklyWorkModel(weeklyWorkModel).pipe(
            mergeMap(() => this.weeklyWorkTimeConcurrencyStorageService.saveWeeklyWorkModel(weeklyWorkModel)),
            tap(() => {
                this.workTimeModel = weeklyWorkModel;
            }),
            map(() => undefined),
        );
    }

    /**
     * This is public function to get the weekly work time model from memory variable
     */

    public getWeeklyWorkTimeList(): WorkdayModel[] {
        return this.workTimeModel.workdayModelList;
    }

    /**
     * This function is to calculate the sum of all the work time Duration of the week
     */
    public getTotalWeekDuration(): number {
        return this.workTimeModel.workdayModelList.reduce(
            (previousValue, currentValue) =>
                previousValue +
                currentValue.submittedTimesheetDuration.asHours() +
                currentValue.currentTimesheetDuration.asHours(),
            0,
        );
    }

    /**
     * Generic method to return work time model for matched date
     *
     * @param date
     */
    private getWorkDayObjectByDate(date: moment.Moment): WorkdayModel | undefined {
        return this.workTimeModel.workdayModelList.find((workDayModel) => workDayModel.workDate.isSame(date, 'days'));
    }

    private updateTotalDurationForSpecificDate(date: moment.Moment, newDuration: moment.Duration): void {
        const workTime = this.getWorkDayObjectByDate(date);

        if (!workTime) {
            this.log.info(
                `WeeklyWorkTimeService:updateTotalDurationForSpecificDate: Work Time model for the date ${date} is not found, Current date is ${moment().format('DD.MM.YYYY')}`,
            );
            return;
        }
        // set all current timesheet durations to 0
        this.getWeeklyWorkTimeList().forEach((workDay) => (workDay.currentTimesheetDuration = moment.duration(0)));
        // set new date's current timesheet value
        workTime.currentTimesheetDuration = moment.duration(newDuration);
    }

    /**
     * We are already storing currentWeek, so comparing stored week number with current day's week number
     * Example:
     * User submits Timesheet i.e on 11th sep 2021 (saturday), the week number is 36
     * User opens application on 14th sep 2021(monday), the week number is 37
     * So, 36!== 37 then it is a new week.
     */
    private isNewWeek(): boolean {
        return this.workTimeModel.currentWeek !== moment().isoWeek();
    }

    public getCurrentDayTotalDuration(): number {
        const currentDayWorkModel = this.getWorkDayObjectByDate(moment());

        if (!isDefined(currentDayWorkModel)) {
            return 0;
        }
        return (
            currentDayWorkModel.submittedTimesheetDuration.asHours() +
            currentDayWorkModel.currentTimesheetDuration.asHours()
        );
    }

    private getTotalDurationOfTimesheetEntries(timesheet: TimesheetModel): moment.Duration {
        return timesheet.getTimesheetEntries().reduce((previousValue, currentValue) => {
            if (
                TimesheetAndExpensesService.isManualEntry(currentValue) &&
                currentValue.taskTypeDropdown.selectedEntry &&
                !this.configService.getTimesheetPauseCodes(currentValue.taskTypeDropdown.selectedEntry.value)
            ) {
                return previousValue.add(
                    moment.duration(currentValue.endDate.value.diff(currentValue.startDate.value)),
                );
            }
            if (
                TimesheetAndExpensesService.isNormalEntry(currentValue) &&
                currentValue.workCode?.value !== TIMERECORDING_TYPE_OF_WORK_CODES.BREAK
            ) {
                return previousValue.add(
                    moment.duration(currentValue.endDate.value.diff(currentValue.startDate.value)),
                );
            }
            return moment.duration(previousValue);
        }, moment.duration(0));
    }
}
